<template>
  <div class="bg-white rounded-lg shadow-sm p-5 sticky top-6">

    <!-- Header -->
    <div class="flex items-center justify-between mb-4">
      <h2 class="text-lg font-medium">Filters</h2>
      <button
        v-if="activeFilterCount > 0"
        type="button"
        class="text-xs text-gray-400 hover:text-gray-600 underline transition-colors"
        @click="handleClearAll"
      >
        Clear all ({{ activeFilterCount }})
      </button>
    </div>

    <!-- Accordion sections -->
    <AccordionRoot type="multiple" :default-value="['basic']" class="space-y-1">

      <!-- ── Basic Filters ───────────────────────────────────────────────── -->
      <AccordionItem value="basic" class="border border-gray-200 rounded-md overflow-hidden">
        <AccordionTrigger class="flex items-center justify-between w-full px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 transition-colors accordion-trigger">
          <span class="flex items-center gap-2">
            Basic Filters
            <span
              v-if="basicFilterCount > 0"
              class="inline-flex items-center justify-center w-4 h-4 rounded-full bg-green-600 text-white text-[10px] font-bold leading-none"
            >{{ basicFilterCount }}</span>
          </span>
          <ChevronDown class="h-4 w-4 text-gray-500 accordion-chevron transition-transform duration-200" />
        </AccordionTrigger>
        <AccordionContent class="accordion-content overflow-hidden">
          <div class="px-3 py-3 space-y-3">

            <div>
              <label class="text-sm font-medium text-gray-700">Trainers</label>
              <div class="mt-1.5">
                <MultiSelectCombobox
                  v-model="selectedTrainers"
                  :options="trainerOptions"
                  placeholder="Select trainers..."
                  track-by="id"
                  label-field="value"
                  :loading="isLoadingTrainers"
                  @search="searchTrainers"
                />
              </div>
            </div>

            <div>
              <label class="text-sm font-medium text-gray-700">Jockeys</label>
              <div class="mt-1.5">
                <MultiSelectCombobox
                  v-model="selectedJockeys"
                  :options="jockeyOptions"
                  placeholder="Select jockeys..."
                  track-by="id"
                  label-field="value"
                  :loading="isLoadingJockeys"
                  @search="searchJockeys"
                />
              </div>
            </div>

            <div>
              <label class="text-sm font-medium text-gray-700">Courses</label>
              <div class="mt-1.5">
                <MultiSelectCombobox
                  v-model="selectedCourses"
                  :options="courseOptions"
                  placeholder="Select courses..."
                  track-by="id"
                  label-field="value"
                  :loading="isLoadingCourses"
                  @search="searchCourses"
                />
              </div>
            </div>

            <div>
              <label class="text-sm font-medium text-gray-700">Regions</label>
              <div class="mt-1.5">
                <MultiSelectCombobox
                  v-model="selectedRegions"
                  :options="regionOptions"
                  placeholder="Select regions..."
                  track-by="value"
                  label-field="value"
                  :loading="isLoadingRegions"
                  @search="searchRegions"
                />
              </div>
            </div>

            <div>
              <label class="text-sm font-medium text-gray-700">Types</label>
              <div class="mt-1.5">
                <MultiSelectCombobox
                  v-model="selectedTypes"
                  :options="typeOptions"
                  placeholder="Select types..."
                  track-by="value"
                  label-field="value"
                  :loading="isLoadingTypes"
                  @search="searchTypes"
                />
              </div>
            </div>

          </div>
        </AccordionContent>
      </AccordionItem>

      <!-- ── Advanced Filters ────────────────────────────────────────────── -->
      <AccordionItem value="advanced" class="border border-gray-200 rounded-md overflow-hidden">
        <AccordionTrigger class="flex items-center justify-between w-full px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 transition-colors accordion-trigger">
          <span class="flex items-center gap-2">
            Advanced Filters
            <span
              v-if="advancedFilterCount > 0"
              class="inline-flex items-center justify-center w-4 h-4 rounded-full bg-green-600 text-white text-[10px] font-bold leading-none"
            >{{ advancedFilterCount }}</span>
          </span>
          <ChevronDown class="h-4 w-4 text-gray-500 accordion-chevron transition-transform duration-200" />
        </AccordionTrigger>
        <AccordionContent class="accordion-content overflow-hidden">
          <div class="px-3 py-3 space-y-3">

            <div>
              <label class="text-sm font-medium text-gray-700">Distance</label>
              <div class="mt-1.5">
                <MultiSelectCombobox
                  v-model="selectedDistances"
                  :options="distanceOptions"
                  placeholder="Select distances..."
                  track-by="value"
                  label-field="value"
                  :loading="isLoadingDistances"
                  @search="searchDistances"
                />
              </div>
            </div>

            <div>
              <label class="text-sm font-medium text-gray-700">Going</label>
              <div class="mt-1.5">
                <MultiSelectCombobox
                  v-model="selectedGoings"
                  :options="goingOptions"
                  placeholder="Select goings..."
                  track-by="value"
                  label-field="value"
                  :loading="isLoadingGoings"
                  @search="searchGoings"
                />
              </div>
            </div>

            <div>
              <label class="text-sm font-medium text-gray-700">Surface</label>
              <div class="mt-1.5">
                <MultiSelectCombobox
                  v-model="selectedSurfaces"
                  :options="surfaceOptions"
                  placeholder="Select surfaces..."
                  track-by="value"
                  label-field="value"
                  :loading="isLoadingSurfaces"
                  @search="searchSurfaces"
                />
              </div>
            </div>

            <div>
              <label class="text-sm font-medium text-gray-700">Age</label>
              <div class="mt-1.5">
                <MultiSelectCombobox
                  v-model="selectedAges"
                  :options="ageOptions"
                  placeholder="Select ages..."
                  track-by="value"
                  label-field="value"
                  :loading="isLoadingAges"
                  @search="searchAges"
                />
              </div>
            </div>

            <div>
              <label class="text-sm font-medium text-gray-700">Sex</label>
              <div class="mt-1.5">
                <MultiSelectCombobox
                  v-model="selectedSexes"
                  :options="sexOptions"
                  placeholder="Select sexes..."
                  track-by="value"
                  label-field="value"
                  :loading="isLoadingSexes"
                  @search="searchSexes"
                />
              </div>
            </div>

            <div>
              <label class="text-sm font-medium text-gray-700">Sires</label>
              <div class="mt-1.5">
                <MultiSelectCombobox
                  v-model="selectedSires"
                  :options="sireOptions"
                  placeholder="Select sires..."
                  track-by="id"
                  label-field="value"
                  :loading="isLoadingSires"
                  @search="searchSires"
                />
              </div>
            </div>

          </div>
        </AccordionContent>
      </AccordionItem>

    </AccordionRoot>

    <!-- ── Action area ─────────────────────────────────────────────────────── -->
    <div class="mt-4">
      <!-- Manual mode: explicit Apply button -->
      <button
        v-if="!autoApply"
        type="button"
        class="w-full py-2 px-4 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-md transition-colors"
        @click="handleApply"
      >
        Apply Filters
      </button>

      <!-- Auto mode: subtle indicator when filters are active -->
      <div
        v-else-if="activeFilterCount > 0"
        class="flex items-center justify-center gap-1.5 text-xs text-gray-400"
      >
        <span class="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse inline-block"></span>
        Filters applied automatically
      </div>
    </div>

  </div>
</template>

<script>
import { watch } from 'vue';
import { AccordionRoot, AccordionItem, AccordionTrigger, AccordionContent } from 'radix-vue';
import { ChevronDown } from 'lucide-vue-next';
import MultiSelectCombobox from '@/components/ui/multi-select-combobox.vue';
import { useFilters } from '@/composables/useFilters';

export default {
  name: 'FilterSidebar',
  components: {
    AccordionRoot, AccordionItem, AccordionTrigger, AccordionContent,
    ChevronDown,
    MultiSelectCombobox,
  },
  props: {
    /**
     * true  → emits 'change' immediately on every filter selection.
     * false → shows an "Apply Filters" button; emits 'apply' when clicked.
     *
     * Toggle this in Analytics.vue via the AUTO_APPLY constant.
     */
    autoApply: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['change', 'apply'],

  setup(props, { emit }) {
    const filters = useFilters();

    // Auto-apply mode: re-emit every time the serialized params change.
    // filterParams is a computed that returns a new object reference on every
    // change, so Vue's watcher fires for each individual selection.
    watch(filters.filterParams, (params) => {
      if (props.autoApply) emit('change', params);
    });

    const handleApply = () => {
      emit('apply', filters.filterParams.value);
    };

    const handleClearAll = () => {
      filters.clearAll();
      // Notify parent regardless of mode so it can reset its data
      const empty = filters.filterParams.value;
      if (props.autoApply) emit('change', empty);
      else emit('apply', empty);
    };

    // Programmatic selection — called by parent via template ref when a chart
    // bar is clicked. Adds the item only if not already selected.
    const addCourse = (item) => {
      if (!item?.id) return;
      const already = filters.selectedCourses.value.some(c => c.id === item.id);
      if (!already) filters.selectedCourses.value = [...filters.selectedCourses.value, item];
    };

    const addTrainer = (item) => {
      if (!item?.id) return;
      const already = filters.selectedTrainers.value.some(t => t.id === item.id);
      if (!already) filters.selectedTrainers.value = [...filters.selectedTrainers.value, item];
    };

    return {
      ...filters,
      handleApply,
      handleClearAll,
      addCourse,
      addTrainer,
    };
  },
};
</script>

<style scoped>
.accordion-content {
  animation: accordion-down 0.2s ease-out;
}

:deep([data-state="closed"] .accordion-content) {
  animation: accordion-up 0.2s ease-out;
}

:deep([data-state="open"] .accordion-chevron) {
  transform: rotate(180deg);
}

@keyframes accordion-down {
  from { height: 0; opacity: 0; }
  to   { height: var(--radix-accordion-content-height); opacity: 1; }
}

@keyframes accordion-up {
  from { height: var(--radix-accordion-content-height); opacity: 1; }
  to   { height: 0; opacity: 0; }
}
</style>
